import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Bed, Maximize2, Bath, Image as ImageIcon, MessageCircle, Phone } from "lucide-react";
import { useLang } from "../context/LangContext";
import { useRate } from "../context/RateContext";
import { formatDA, formatEUR } from "../lib/i18n";
import { localized } from "../lib/api";

function Tag({ children, className = "" }) {
  return <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-md ${className}`}>{children}</span>;
}

export default function PropertyCard({ item }) {
  const { t, lang } = useLang();
  const { rate } = useRate();
  const [loaded, setLoaded] = useState(false);

  const hasImage = item.images && item.images.length > 0;
  const typeLabel = { apartment: t("apartment"), house: t("house"), land: t("land") }[item.type] || item.type;
  const title = localized(item, "title", lang);

  const rawWa = (item.whatsapp || item.phone || "").replace(/[^0-9]/g, "");
  const rawTel = (item.phone || item.whatsapp || "").replace(/[^0-9+]/g, "");
  const waMsg = encodeURIComponent(`Bonjour, je suis intéressé(e) par l'annonce ${item.reference || ""} — ${title}`);
  const waHref = rawWa ? `https://wa.me/${rawWa}?text=${waMsg}` : null;
  const telHref = rawTel ? `tel:${rawTel}` : null;

  const stop = (e) => { e.stopPropagation(); };

  return (
    <div className="relative bg-white rounded-2xl border border-slate-200/70 overflow-hidden hover:shadow-[0_20px_40px_-15px_rgba(27,143,90,0.18)] hover:-translate-y-1 transition-all duration-300 group" data-testid={`property-card-${item.id}`}>
      <Link to={`/property/${item.id}`} aria-label={title} className="absolute inset-0 z-0" data-testid={`card-link-${item.id}`} />
      <div className="aspect-[4/3] w-full relative overflow-hidden bg-slate-100">
        {hasImage ? (
          <img src={item.images[0]} alt={title} onLoad={() => setLoaded(true)} className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 img-fade ${loaded ? "loaded" : ""}`} />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-slate-400">
            <ImageIcon size={36} strokeWidth={1.4} /><span className="text-xs mt-2">No image</span>
          </div>
        )}

        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5 z-10">
          <Tag className="bg-white/95 backdrop-blur text-[#1B8F5A]">
            {item.transaction === "sale" ? t("forSale") : t("forRent")}
          </Tag>
          {item.featured && <span data-testid="agency-badge" className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-md bg-[#F59E0B] text-white">✓ {t("verified")}</span>}
          {item.condition === "new" && <Tag className="bg-[#1B8F5A] text-white">{t("new")}</Tag>}
          {item.payment_type === "installment" && <Tag className="bg-amber-100 text-amber-800">{t("installment")}</Tag>}
        </div>

        {item.reference && (
          <div className="absolute top-3 right-3 z-10">
            <Tag className="bg-black/60 backdrop-blur text-white">{item.reference}</Tag>
          </div>
        )}
      </div>

      <div className="p-5">
        <div className="flex items-baseline justify-between gap-3">
          <div>
            {item.price_on_request ? (
              <div className="text-xl font-bold text-[#F59E0B]">{t("priceOnRequest")}</div>
            ) : (
              <>
                <div className="text-xl font-bold text-[#F59E0B]" data-testid="card-price-da">
                  {formatDA(item.price_da)}{item.transaction === "rent" && <span className="text-xs font-medium text-slate-500">{t("perMonth")}</span>}
                </div>
                <div className="text-xs text-slate-500 mt-0.5" title={t("eurNote")}>{formatEUR(item.price_da, rate)}</div>
              </>
            )}
          </div>
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{typeLabel}</span>
        </div>

        <h3 className="text-base font-semibold text-[#111827] mt-3 line-clamp-1">{title}</h3>
        <div className="text-sm text-slate-500 mt-1 flex items-center gap-1">
          <MapPin size={13} />
          <span className="truncate">{item.neighborhood ? `${item.neighborhood}, ` : ""}{item.city}, {item.wilaya}</span>
        </div>

        <div className="flex items-center gap-4 text-sm text-slate-600 mt-4 pt-4 border-t border-slate-100">
          <span className="flex items-center gap-1.5"><Maximize2 size={14} />{item.surface} m²</span>
          {item.rooms > 0 && <span className="flex items-center gap-1.5"><Bed size={14} />{item.rooms}</span>}
          {item.bathrooms > 0 && <span className="flex items-center gap-1.5"><Bath size={14} />{item.bathrooms}</span>}
        </div>

        {(item.title_deed === "available" || item.land_registry === "available" || item.negative_cert === "available") && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {item.title_deed === "available" && <Tag className="bg-emerald-50 text-emerald-700">{t("titleDeed")}</Tag>}
            {item.land_registry === "available" && <Tag className="bg-blue-50 text-blue-700">{t("landRegistry")}</Tag>}
            {item.negative_cert === "available" && <Tag className="bg-teal-50 text-teal-700">{t("negativeCert")}</Tag>}
          </div>
        )}

        {(waHref || telHref) && (
          <div className="relative z-10 mt-4 flex gap-2">
            {waHref && (
              <a
                href={waHref}
                target="_blank"
                rel="noreferrer"
                onClick={stop}
                className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#25D366] hover:bg-[#1EBE57] text-white text-sm font-semibold py-2.5 rounded-xl transition"
                data-testid={`card-whatsapp-${item.id}`}
                aria-label="WhatsApp"
              >
                <MessageCircle size={15} /> {t("messageWhatsapp")}
              </a>
            )}
            {telHref && (
              <a
                href={telHref}
                onClick={stop}
                className="flex-1 inline-flex items-center justify-center gap-1.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 text-[#111827] text-sm font-semibold py-2.5 rounded-xl transition"
                data-testid={`card-call-${item.id}`}
                aria-label="Call"
              >
                <Phone size={14} /> {t("callNow")}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
